import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  generateWordSearch,
  findMatchingWord,
  scoreForFoundWord,
  crosswordAllWordsBonus,
  tapCrosswordLineSelection,
  getStarCount,
  getDailyCrosswordConfig,
  crosswordMiniMaxHintsPerPuzzle,
} from './crosswordUtils';
import { useAuth } from '../../context/AuthContext';
import { recordCrosswordPlay } from '../../firebase/crosswordService';
import CozyPlayShell from '../../components/CozyPlayShell/CozyPlayShell';
import { HowToPlay } from './HowToPlay';
import './Crossword.css';
import {
  isE2E,
  resetE2eBridge,
  e2eSetCrossword,
} from '../../e2e/globals';

const HOW_TO_PLAY_KEY = 'crosswordHowToPlaySeen';

function Crossword() {
  const { user, refreshProfile } = useAuth();
  const crosswordLineDirRef = useRef(null);
  const [practice, setPractice] = useState(false);
  const [puzzle, setPuzzle] = useState(() => {
    const cfg = getDailyCrosswordConfig();
    return generateWordSearch(cfg.size, cfg.seed);
  });
  const [foundWords, setFoundWords] = useState([]);
  const [hintedWords, setHintedWords] = useState([]);
  const [hintsRemaining, setHintsRemaining] = useState(() => crosswordMiniMaxHintsPerPuzzle);
  const [score, setScore] = useState(0);
  const [selection, setSelection] = useState([]);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  const resetPlayState = useCallback((nextPuzzle) => {
    setPuzzle(nextPuzzle);
    setFoundWords([]);
    setHintedWords([]);
    setHintsRemaining(crosswordMiniMaxHintsPerPuzzle);
    setScore(0);
    setSelection([]);
    setMessage('');
    setSubmitted(false);
    crosswordLineDirRef.current = null;
  }, []);

  const applyMode = useCallback(
    (nextPractice) => {
      if (nextPractice === practice) return;
      setPractice(nextPractice);
      if (nextPractice) {
        const seed = Math.floor(Math.random() * 1_000_000_000);
        resetPlayState(generateWordSearch(8, seed));
      } else {
        const cfg = getDailyCrosswordConfig();
        resetPlayState(generateWordSearch(cfg.size, cfg.seed));
      }
    },
    [practice, resetPlayState],
  );

  useEffect(() => {
    if (!localStorage.getItem(HOW_TO_PLAY_KEY)) setShowHowToPlay(true);
  }, []);

  useEffect(() => {
    if (!isE2E()) return;
    resetE2eBridge();
    const wp = {};
    puzzle.wordPositions.forEach((cells, w) => {
      wp[w] = cells;
    });
    e2eSetCrossword({
      hiddenWords: [...puzzle.hiddenWords],
      wordPositions: wp,
      size: puzzle.grid.length,
    });
  }, [puzzle]);

  const selectionRef = useRef(selection);
  const foundWordsRef = useRef(foundWords);
  const hintedWordsRef = useRef(hintedWords);
  const gridRef = useRef(puzzle.grid);
  const wordPositionsRef = useRef(puzzle.wordPositions);
  selectionRef.current = selection;
  foundWordsRef.current = foundWords;
  hintedWordsRef.current = hintedWords;
  gridRef.current = puzzle.grid;
  wordPositionsRef.current = puzzle.wordPositions;

  const { grid, hiddenWords, wordPositions } = puzzle;
  const size = grid.length;

  const getCellId = (r, c) => `${r}-${c}`;

  const hintGlowKeys = useMemo(() => {
    const keys = new Set();
    for (const wUpper of hiddenWords) {
      const wl = wUpper.toLowerCase();
      if (foundWords.includes(wl)) continue;
      if (!hintedWords.includes(wl)) continue;
      const cells = wordPositions.get(wUpper);
      if (!cells) continue;
      for (const [r, c] of cells) keys.add(getCellId(r, c));
    }
    return keys;
  }, [hiddenWords, foundWords, hintedWords, wordPositions]);

  const hintCandidateCount = useMemo(
    () =>
      hiddenWords.filter(
        (w) =>
          !foundWords.includes(w.toLowerCase()) && !hintedWords.includes(w.toLowerCase()),
      ).length,
    [hiddenWords, foundWords, hintedWords],
  );
  const isInSelection = (r, c) => selection.some(([sr, sc]) => sr === r && sc === c);
  const isLastSelected = (r, c) => {
    if (selection.length === 0) return false;
    const [lr, lc] = selection[selection.length - 1];
    return lr === r && lc === c;
  };

  const handleCellTap = (r, c) => {
    setMessage('');
    setSelection((prev) => {
      const next = tapCrosswordLineSelection(
        prev,
        size,
        r,
        c,
        crosswordLineDirRef.current,
      );
      crosswordLineDirRef.current = next.lineDir;
      return next.selection;
    });
  };

  const clearWordSelection = () => {
    crosswordLineDirRef.current = null;
    setSelection([]);
    setMessage('');
  };

  const submitCurrentWord = useCallback(() => {
    const sel = selectionRef.current;
    if (sel.length < 4) {
      setMessage('Select at least 4 letters in a straight line, then tap Submit word');
      crosswordLineDirRef.current = null;
      setSelection([]);
      return;
    }

    const matched = findMatchingWord(sel, gridRef.current, wordPositionsRef.current);
    if (!matched) {
      setMessage('Not a hidden word');
      crosswordLineDirRef.current = null;
      setSelection([]);
      return;
    }

    const wordLower = matched.toLowerCase();
    if (foundWordsRef.current.includes(wordLower)) {
      setMessage('Already found');
      crosswordLineDirRef.current = null;
      setSelection([]);
      return;
    }

    const hadRevealHint = hintedWordsRef.current.includes(wordLower);
    const completesAll = foundWordsRef.current.length + 1 === hiddenWords.length;
    const wordPts = scoreForFoundWord(matched, hadRevealHint);
    setFoundWords((prev) => [...prev, wordLower].sort());
    setScore((prev) => prev + wordPts + (completesAll ? crosswordAllWordsBonus() : 0));
    crosswordLineDirRef.current = null;
    setSelection([]);
    setMessage('');
  }, [hiddenWords.length]);

  const applyHint = useCallback(() => {
    setMessage('');
    setHintsRemaining((hintsLeft) => {
      if (hintsLeft <= 0) return hintsLeft;
      const found = foundWordsRef.current;
      const hinted = hintedWordsRef.current;
      const candidates = hiddenWords.filter(
        (w) => !found.includes(w.toLowerCase()) && !hinted.includes(w.toLowerCase()),
      );
      if (candidates.length === 0) return hintsLeft;
      const pick = candidates[Math.floor(Math.random() * candidates.length)];
      const wl = pick.toLowerCase();
      setHintedWords((prev) => (prev.includes(wl) ? prev : [...prev, wl].sort()));
      return hintsLeft - 1;
    });
  }, [hiddenWords]);

  const currentWordLetters = selection.length
    ? selection.map(([r, c]) => grid[r][c]).join('')
    : '';

  const starCount = getStarCount(score, size);
  const allFound = foundWords.length === hiddenWords.length;

  const handleSubmit = async () => {
    if (practice) return;
    setSubmitted(true);
    if (!user && !isE2E()) return;
    try {
      await recordCrosswordPlay(score, foundWords.length, starCount);
      if (user) await refreshProfile();
    } catch (e) {
      console.error('Failed to save crossword play:', e);
    }
  };

  return (
    <CozyPlayShell
      className="crossword"
      title="Hidden Words"
      mode={practice ? 'practice' : 'daily'}
      onModeChange={(m) => applyMode(m === 'practice')}
      onHowToPlay={() => setShowHowToPlay(true)}
      subtitle={`Find all ${hiddenWords.length} hidden words (across, down, diagonal)`}
      meta={
        <>
          <span className="crossword-score">Score: {score}</span>
          <span className="crossword-words">{foundWords.length} / {hiddenWords.length} words</span>
          <span className="crossword-hints-meta">{hintsRemaining} hints</span>
          <span className="crossword-stars">
            {[1, 2, 3].map((i) => (
              <span key={i} className={starCount >= i ? 'star filled' : 'star'}>★</span>
            ))}
          </span>
        </>
      }
      headerExtra={
        !practice ? (
          <>
            <button
              type="button"
              className="crossword-submit-btn"
              onClick={handleSubmit}
              disabled={submitted}
            >
              {submitted ? '✓ Submitted' : 'Submit Score'}
            </button>
            {!user && (
              <p className="crossword-signin-hint">Sign in to save your score!</p>
            )}
          </>
        ) : (
          <p className="crossword-practice-note">Practice grid — scores are not saved.</p>
        )
      }
    >
      {showHowToPlay && (
        <HowToPlay
          onClose={() => {
            setShowHowToPlay(false);
            localStorage.setItem(HOW_TO_PLAY_KEY, '1');
          }}
        />
      )}

      <div className="crossword-layout">
        <div className="crossword-grid-wrap" style={{ '--size': size }}>
          <div className="crossword-grid">
            {grid.map((row, r) =>
              row.map((letter, c) => (
                <button
                  key={getCellId(r, c)}
                  type="button"
                  data-testid={`crossword-cell-${r}-${c}`}
                  className={`crossword-cell ${isInSelection(r, c) ? 'selected' : ''} ${hintGlowKeys.has(getCellId(r, c)) && !isInSelection(r, c) ? 'hint-glow' : ''} ${isLastSelected(r, c) ? 'last' : ''}`}
                  aria-label={`Letter ${letter}${isInSelection(r, c) ? ', selected' : ''}`}
                  onClick={() => handleCellTap(r, c)}
                >
                  {letter}
                </button>
              ))
            )}
          </div>
          <div className="crossword-word-toolbar" aria-live="polite">
            <div className="crossword-find-words-panel">
              <h3 className="crossword-find-words-title" id="crossword-find-words-heading">
                Words to find ({hiddenWords.length})
              </h3>
              <div
                className="crossword-solution-chips"
                aria-labelledby="crossword-find-words-heading"
                role="group"
              >
                {hiddenWords.map((w) => {
                  const wl = w.toLowerCase();
                  const got = foundWords.includes(wl);
                  const hinted = hintedWords.includes(wl) && !got;
                  return (
                    <span
                      key={w}
                      className={`crossword-solution-chip ${got ? 'found' : ''} ${hinted ? 'hinted' : ''}`}
                      aria-label={hinted ? `${w}, hinted` : `${w}${got ? ', found' : ', not yet found'}`}
                    >
                      {hinted ? <span aria-hidden className="crossword-chip-lightbulb">💡 </span> : null}
                      {w.toLowerCase()}
                    </span>
                  );
                })}
              </div>
            </div>
            <span className="crossword-current-word">{currentWordLetters || '—'}</span>
            <div className="crossword-word-actions">
              <button
                type="button"
                className="crossword-submit-word-btn"
                disabled={selection.length < 4}
                onClick={submitCurrentWord}
              >
                Submit word
              </button>
              <button type="button" className="crossword-clear-word-btn" onClick={clearWordSelection}>
                Clear
              </button>
              <button
                type="button"
                className="crossword-hint-btn"
                disabled={hintsRemaining <= 0 || hintCandidateCount === 0 || allFound}
                onClick={applyHint}
                title="Highlight one unfound word's path (half points when you find it, minimum 5)"
              >
                Hint ({hintsRemaining})
              </button>
            </div>
            <p className="crossword-word-hint">
              Tap letters in a straight line (including diagonals). Hidden words are at least four letters. Tap{' '}
              <strong>Submit word</strong> when your selection matches one; use <strong>Clear</strong> to reset. Tap the
              last letter again to undo one step. <strong>Hint ({crosswordMiniMaxHintsPerPuzzle})</strong> highlights one
              unfound word for half score (minimum 5) when found.
            </p>
          </div>
        </div>

        <aside className="crossword-sidebar">
          <div className="crossword-words-list">
            <h3>Found words</h3>
            <ul>
              {foundWords.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
            {message && <p className="crossword-feedback">{message}</p>}
          </div>
          {allFound && (
            <div className="crossword-complete">
              🎉 All words found!
            </div>
          )}
        </aside>
      </div>
    </CozyPlayShell>
  );
}

export default Crossword;
